/**
 * Persona metadata registry — SPEC-FRONTEND_V4 §9.
 * 6 personas used to drive LNav highlighting, tool preselection, and hero copy.
 */

export interface Persona {
  id: string;
  name: string;
  emoji: string;
  color: string; // CSS color variable reference
  description: string;
  tools: string[]; // Tool IDs
  primaryFuncs: string[]; // Function display names
  highlightedNavItems: string[];
}

export const personas: Record<string, Persona> = {
  'dot-executive': {
    id: 'dot-executive',
    name: 'DOT Executive',
    emoji: '👔',
    color: 'var(--blue)',
    description:
      'Department head overseeing multi-modal infrastructure strategy',
    tools: ['scenarios', 'intel-brief', 'assessment'],
    primaryFuncs: ['Planning', 'Budgeting', 'Risk Mgmt', 'Projects'],
    highlightedNavItems: ['Planning', 'Budgeting', 'Risk Mgmt', 'Projects'],
  },
  'program-manager': {
    id: 'program-manager',
    name: 'Program Manager',
    emoji: '📋',
    color: 'var(--teal)',
    description:
      'Manages delivery of infrastructure programs and initiatives',
    tools: ['workflow', 'calculator', 'assessment'],
    primaryFuncs: ['Planning', 'Budgeting', 'Projects', 'Compliance'],
    highlightedNavItems: ['Planning', 'Budgeting', 'Projects', 'Compliance'],
  },
  'analyst-planner': {
    id: 'analyst-planner',
    name: 'Analyst/Planner',
    emoji: '📊',
    color: 'var(--slate)',
    description:
      'Data-driven analyst supporting strategic planning and forecasting',
    tools: ['calculator', 'scenarios', 'intel-feed'],
    primaryFuncs: ['Planning', 'Data & Analytics', 'Auditing'],
    highlightedNavItems: ['Planning', 'Data & Analytics', 'Auditing'],
  },
  'insurance-risk-manager': {
    id: 'insurance-risk-manager',
    name: 'Insurance Risk Manager',
    emoji: '🛡️',
    color: 'var(--amber)',
    description: 'Identifies and mitigates operational and financial risks',
    tools: ['assessment', 'scenarios', 'intel-brief'],
    primaryFuncs: ['Risk Mgmt', 'Safety', 'Compliance', 'Assets'],
    highlightedNavItems: ['Risk Mgmt', 'Safety', 'Compliance', 'Assets'],
  },
  'engineering-lead': {
    id: 'engineering-lead',
    name: 'Engineering Lead',
    emoji: '⚙️',
    color: 'var(--amber)',
    description:
      'Technical lead responsible for project design and engineering standards',
    tools: ['gis-view', 'calculator', 'workflow'],
    primaryFuncs: ['Engineering', 'Projects', 'Safety', 'Operations'],
    highlightedNavItems: ['Engineering', 'Projects', 'Safety', 'Operations'],
  },
  'contractor-constructor': {
    id: 'contractor-constructor',
    name: 'Contractor/Constructor',
    emoji: '🏗️',
    color: 'var(--red)',
    description:
      'Field-level execution of infrastructure projects and maintenance',
    tools: ['gis-view', 'workflow', 'assessment'],
    primaryFuncs: ['Projects', 'Safety', 'Operations', 'Auditing'],
    highlightedNavItems: ['Projects', 'Safety', 'Operations', 'Auditing'],
  },
};

export const DEFAULT_PERSONA_ID = 'dot-executive';

export function getPersonaList(): Persona[] {
  return Object.values(personas);
}
